import { getNumberSuffix, makeClassName, pluralize } from '@siteless/util';
import avatars from '../config/avatars';
import ObjectiveStateResource from '../resources/ObjectiveState';
import useCopy from '../util/useCopy';
import TrophyIcon from './TrophyIcon';
import { forwardRef } from 'react';
import { PopWrapper } from './PopWrapper';
import { oscarDatabase } from '../config/constants';
import LetterboxdIcon from './LetterboxdIcon';
import CrownIcon from './CrownIcon';

type PropsType = {
  points?: number;
  rank?: number;
  hasScoringStarted?: boolean;
  useStrictScore?: boolean;
  incompleteTier?: boolean;
  ownerUserId?: string;
  userBallots: {
    key: string;
    ArtworkComponent: (typeof avatars)[keyof typeof avatars]['ArtworkComponent'];
    handle?: string;
    avatarBackgroundHex?: string | null;
    letterboxdId?: string | null;
  }[];
  onSelectBallot?: (ballotKey: string) => void;
};

export let ClusterBallotTier = forwardRef<HTMLDivElement, PropsType>(
  (
    {
      points,
      rank,
      hasScoringStarted,
      userBallots,
      useStrictScore,
      onSelectBallot,
      incompleteTier,
      ownerUserId,
    },
    ref,
  ) => {
    let copy = useCopy();
    let objectiveState = ObjectiveStateResource.use();

    return (
      <div className="relative flex flex-col pb-4">
        {(hasScoringStarted || incompleteTier) && (
          <div className="bg-neutral-0 sticky top-1 z-20 mb-4 flex items-center justify-center self-center rounded-full border-4 border-neutral-100 px-3 py-1 text-sm font-medium text-purple-900 shadow-lg">
            {(rank || 0) === 1 && (points || 0) > 0 && (
              <TrophyIcon className="mr-2 h-5 w-5" />
            )}
            {incompleteTier ? (
              copy['Incomplete Ballots 🥲']
            ) : objectiveState.didCeremonyEnd && rank === 1 ? (
              pluralize({ hideCount: true, count: userBallots.length, singular: copy.Winner, plural: copy.winners }).toUpperCase()
            ) : (
              <>{getNumberSuffix(rank || 0)} Place</>
            )}
          </div>
        )}

        <div className="mb-4 text-center text-xs text-purple-300">
          {typeof points === 'number'
            ? useStrictScore
              ? `${points} / ${
                  Object.keys(oscarDatabase.nomineeCategories).length
                }`
              : pluralize({
                  count: points,
                  singular: copy.point,
                })
            : null}
        </div>

        <div className="mb-6 flex flex-wrap justify-center gap-3 px-2">
          {userBallots.map(
            ({ key, ArtworkComponent, handle, avatarBackgroundHex, letterboxdId }) => {
              return (
                <div
                  key={key}
                  className={makeClassName(
                    'flex flex-col items-center justify-center rounded-lg p-2 transition-transform hover:scale-105',
                  )}
                  onClick={() => {
                    onSelectBallot?.(key);
                  }}
                >
                  <PopWrapper disabled={incompleteTier}>
                    <ArtworkComponent
                      className="h-16 w-16 shadow-md md:h-20 md:w-20"
                      backgroundColor={avatarBackgroundHex || undefined}
                    />
                  </PopWrapper>

                  <div className="__handle mt-2 max-w-full truncate text-center text-sm">
                    {handle}
                    {ownerUserId === key && (
                      <CrownIcon className="ml-1 inline-block h-4 w-4 text-purple-400" />
                    )}
                    {letterboxdId && (
                      <LetterboxdIcon className="ml-1 inline-block h-4 w-4 text-purple-400" />
                    )}
                  </div>
                </div>
              );
            },
          )}
        </div>
      </div>
    );
  },
);
